import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteHomeBanner, getAdminHomeBanners } from 'apis/adminHomeBanners';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const HomeBannersList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const { data: languages = [] } = useActiveLanguagesQuery();
    const queryParams = useMemo(() => ({
        pageNumber: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter]);

    const { data = { items: [], total: 0, currentPage: 1, pageSize: 10 }, isFetching, error } = useAdminListQuery('home-banners', queryParams, getAdminHomeBanners);
    const deleteMutation = useAdminDeleteMutation('home-banners', deleteHomeBanner);

    useEffect(() => { setCurrentPage(1); }, [keyword, isActiveFilter, languageCodeFilter]);
    useEffect(() => { if (error) toast.error(error.message || 'Tải dữ liệu thất bại'); }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res?.success) {
                toast.success('Xóa thành công');
            } else {
                toast.error(res?.message || 'Xóa thất bại');
            }
        } catch {
            toast.error('Xóa thất bại');
        }
        setDeleteId(null);
    };

    const columns = [
        { field: 'id', header: 'ID' },
        {
            header: 'Hình ảnh',
            render: (row) => row.image ? (
                <img src={row.image} alt={row.title || 'banner'} style={{ width: '120px', height: '60px', objectFit: 'cover', borderRadius: '6px' }} />
            ) : <span style={{ color: '#aaa', fontSize: '12px' }}>Chưa có</span>,
        },
        {
            header: 'Tiêu đề (Title)',
            render: (row) => (
                <div>
                    <div style={{ fontWeight: 600, color: '#1a2b22', fontSize: '14px' }}>{row.title || '—'}</div>
                    {row.eyebrow && <div style={{ fontSize: '12px', color: '#00a859', marginTop: '2px' }}>{row.eyebrow}</div>}
                </div>
            ),
        },
        {
            header: 'Mô tả (Lead)',
            render: (row) => <div style={{ fontSize: '13px', color: '#555', maxWidth: '300px' }}>{row.lead || '—'}</div>,
        },
        { field: 'sortOrder', header: 'Thứ tự' },
        {
            header: 'Trạng thái',
            render: (row) => (
                <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>
                    {row.isActive ? 'Hoạt động' : 'Ẩn'}
                </span>
            ),
        },
        {
            header: 'Thao tác',
            render: (row) => (
                <div className="action-buttons">
                    <button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/home-banners/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button>
                    <button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Xóa"><FiTrash2 size={15} /></button>
                </div>
            ),
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Quản lý Banner Trang chủ (Hero Slider)"
                subtitle="Danh sách hình ảnh banner slider ở đầu trang chủ"
                action={<Button onClick={() => navigate('/admin/home-banners/create')}>+ Thêm Banner mới</Button>}
            />
            <div className="filters-bar">
                <div className="filter-item">
                    <Input placeholder="Tìm kiếm theo tiêu đề..." value={keyword} onChange={(e) => setKeyword(e.target.value)} />
                </div>
                <div className="filter-item">
                    <Select
                        value={isActiveFilter}
                        onChange={(e) => setIsActiveFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả trạng thái' },
                            { value: 'true', label: 'Hoạt động' },
                            { value: 'false', label: 'Ẩn' },
                        ]}
                    />
                </div>
                <div className="filter-item">
                    {languages.length > 0 && (
                        <Select
                            value={languageCodeFilter}
                            onChange={(e) => setLanguageCodeFilter(e.target.value)}
                            options={languages.map(l => ({ value: l.code, label: l.name }))}
                        />
                    )}
                </div>
            </div>
            <div className="table-card">
                <Table
                    columns={columns}
                    data={data.items || []}
                    loading={isFetching}
                    pagination={{
                        currentPage: data.currentPage || 1,
                        totalPages: Math.ceil((data.total || 0) / (data.pageSize || 10)) || 1,
                        onPageChange: setCurrentPage,
                    }}
                />
            </div>
            <ConfirmationDialog
                isOpen={!!deleteId}
                title="Xác nhận xóa Banner"
                message="Bạn có chắc chắn muốn xóa Banner này không?"
                onConfirm={handleDelete}
                onCancel={() => setDeleteId(null)}
                confirmLoading={deleteMutation.isPending}
            />
        </div>
    );
};

export default HomeBannersList;
